"use client";

/**
 * Progress medallion shown after the user clicks Preview.
 *
 * The backend now prepares the preview through a Temporal workflow
 * (reveal → prepare → render) and the modal polls the session for live
 * progress, so BOTH phases render determinate progress when counts are
 * available:
 *
 *   - `mode="revealing"`  — contact reveal fan-out; counts are revealed
 *     profiles (`done / total`). Contact icon in the center.
 *   - `mode="generating"` — per-cell content render; counts are rendered
 *     preview cells. Preview icon in the center.
 *
 * The big center counter animates via NumberFlow so rapid increments read
 * as one smooth roll instead of flickering text. When no counts exist yet
 * (workflow still booting, or everything was cached) the ring spins
 * indeterminately around the phase icon.
 */

import NumberFlow from "@number-flow/react";
import { motion } from "framer-motion";

import { Icon, type IconName } from "@/components/ui/icon";

import { ENROLLMENT_ACCENT, ENROLLMENT_ACCENT_TEXT } from "../../lib/theme";

const SIZE = 128;
const STROKE = 6;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const PHASE_ICON: Record<"revealing" | "generating", IconName> = {
  revealing: "IconContacts",
  generating: "IconEyeSparkle",
};

export interface ConfigureProgressProps {
  mode: "revealing" | "generating";
  /** Determinate fraction 0..1; ignored while `total` is absent. */
  value: number;
  /** Completed units (revealed contacts / rendered previews). */
  done?: number;
  /** Total units; absent or 0 renders the indeterminate ring. */
  total?: number;
  caption: string;
  detail?: string;
}

export function ConfigureProgress({
  mode,
  value,
  done,
  total,
  caption,
  detail,
}: ConfigureProgressProps) {
  const hasCounts = typeof total === "number" && total > 0;
  const indeterminate = !hasCounts;
  const clamped = Math.max(0, Math.min(1, value));
  const offset = CIRCUMFERENCE * (1 - clamped);

  return (
    <div
      className="flex min-h-0 flex-1 flex-col items-center justify-center gap-6 px-6 py-12"
      role="status"
      aria-live="polite"
    >
      <div className="relative" style={{ width: SIZE, height: SIZE }}>
        <motion.svg
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className="-rotate-90"
          animate={indeterminate ? { rotate: [0, 360] } : undefined}
          transition={
            indeterminate
              ? { repeat: Infinity, duration: 1.1, ease: "linear" }
              : undefined
          }
        >
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke="currentColor"
            strokeWidth={STROKE}
            className="text-foreground/10"
          />
          <motion.circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke={ENROLLMENT_ACCENT.base}
            strokeWidth={STROKE}
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={indeterminate ? CIRCUMFERENCE * 0.7 : offset}
            animate={indeterminate ? undefined : { strokeDashoffset: offset }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          />
        </motion.svg>

        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
          {hasCounts ? (
            <>
              <Icon
                name={PHASE_ICON[mode]}
                size={18}
                className={ENROLLMENT_ACCENT_TEXT}
              />
              <NumberFlow
                value={done ?? 0}
                className="text-3xl font-semibold leading-none tabular-nums text-heading"
              />
              <span className="text-[11px] font-medium text-muted-foreground tabular-nums">
                of {total}
              </span>
            </>
          ) : (
            <Icon
              name={PHASE_ICON[mode]}
              size={32}
              className={ENROLLMENT_ACCENT_TEXT}
            />
          )}
        </div>
      </div>

      <div className="flex flex-col items-center gap-1 text-center">
        <span className="text-sm font-medium text-heading">{caption}</span>
        {detail && (
          <span className="text-xs text-muted-foreground tabular-nums">
            {detail}
          </span>
        )}
      </div>
    </div>
  );
}
